import { loadsIngestServiceAxios } from 'setup/SetupAxios'

export type TelegramAccount = {
    id: number
    phone: string
    status: string
    createdAt: string
}

export type WatchedChat = {
    id: number
    peerId: string
    username?: string | null
    title?: string | null
    isChannel: boolean
    brokerName?: string | null
    createdAt: string
}

export type TelegramGroup = {
    id: number
    peerId: string
    joinLink: string | null
    status: 'active' | 'inactive'
    originalName: string | null
    brokerCompanyName: string | null
    createdAt: string
    updatedAt: string
    watchingAccountsCount?: number
    watchingAccounts?: TelegramAccount[]
}

type WatchAddChatPayload = {
    phone: string
    peerId: string
    username?: string
    title?: string
    isChannel?: boolean
}

export const sendCode = async (phone: string): Promise<void> => {
    await loadsIngestServiceAxios.post('/telegram/sendCode', { phone })
}

export const signIn = async (phone: string, code: string, password?: string): Promise<void> => {
    await loadsIngestServiceAxios.post('/telegram/signIn', { phone, code, password })
}

export const logout = async (phone: string): Promise<void> => {
    await loadsIngestServiceAxios.post(`/telegram/logout`, { phone })
}

// ---- Watch APIs ----
export const addWatchedChat = async (payload: WatchAddChatPayload): Promise<void> => {
    await loadsIngestServiceAxios.post('/telegram/addChat', payload)
}

// ---- NEW: Lists (adjust endpoints if yours differ) ----
export const listAccounts = async (): Promise<TelegramAccount[]> => {
    const { data } = await loadsIngestServiceAxios.get('/telegram/accounts')
    // Expecting { ok: true, data: TelegramAccount[] }
    return data?.data ?? []
}

export const listWatched = async (phone: string): Promise<WatchedChat[]> => {
    const { data } = await loadsIngestServiceAxios.get(`/telegram/list?phone=${encodeURIComponent(phone)}`)
    return data?.data ?? []
}

const resolvePeerApi = async (phone: string, input: string) => {
    const { data } = await loadsIngestServiceAxios.post('/telegram/resolve', { phone, input })
    return data // { ok, data: { peerId, title, username, isChannel, ... } }
}

export const recentDialogsApi = async (phone: string, limit = 30) => {
    const { data } = await loadsIngestServiceAxios.get(`/telegram/dialogs?phone=${encodeURIComponent(phone)}&limit=${limit}`)
    return data // { ok, data: Array<{peerId,title,username,...}> }
}

export const removeWatchedChat = async (phone: string, peerId: string): Promise<void> => {
    await loadsIngestServiceAxios.post('/telegram/removeChat', { phone, peerId })
}

export const updateChatBrokerName = async (phone: string, peerId: string, brokerName: string | null): Promise<void> => {
    await loadsIngestServiceAxios.patch('/telegram/updateChatBrokerName', { phone, peerId, brokerName })
}

export const joinChatByLink = async (phone: string, link: string) => {
    const { data } = await loadsIngestServiceAxios.post('/telegram/joinChat', { phone, link })
    return data // { ok, data: { peerId, title, username, isChannel } }
}

export const watchAllChats = async (phone: string) => {
    const { data } = await loadsIngestServiceAxios.post('/telegram/watchAll', { phone })
    return data // { ok, data: { added: number, chats: Array } }
}

export const unwatchAllChats = async (phone: string) => {
    const { data } = await loadsIngestServiceAxios.post('/telegram/unwatchAll', { phone })
    return data // { ok, data: { removed: number } }
}

export const startWatcher = async (phone: string): Promise<void> => {
    await loadsIngestServiceAxios.post('/telegram/startWatcher', { phone })
}

export const stopWatcher = async (phone: string): Promise<void> => {
    await loadsIngestServiceAxios.post('/telegram/stopWatcher', { phone })
}

// =============================================================================
// Groups API (new architecture)
// =============================================================================

export const listGroups = async (): Promise<TelegramGroup[]> => {
    const { data } = await loadsIngestServiceAxios.get('/telegram/groups')
    return data?.data ?? []
}

export const createGroup = async (payload: {
    peerId: string
    joinLink?: string | null
    originalName?: string | null
    brokerCompanyName?: string | null
    status?: string
}): Promise<TelegramGroup> => {
    const { data } = await loadsIngestServiceAxios.post('/telegram/groups', payload)
    return data?.data
}

export const getGroup = async (peerId: string): Promise<TelegramGroup> => {
    const { data } = await loadsIngestServiceAxios.get(`/telegram/groups/${encodeURIComponent(peerId)}`)
    return data?.data
}

export const updateGroup = async (
    peerId: string,
    payload: {
        joinLink?: string | null
        status?: string
        originalName?: string | null
        brokerCompanyName?: string | null
    }
): Promise<TelegramGroup> => {
    const { data } = await loadsIngestServiceAxios.patch(`/telegram/groups/${encodeURIComponent(peerId)}`, payload)
    return data?.data
}

export const deleteGroupApi = async (peerId: string): Promise<void> => {
    await loadsIngestServiceAxios.delete(`/telegram/groups/${encodeURIComponent(peerId)}`)
}

// Account-Group relationship
const listAccountsForGroup = async (peerId: string): Promise<TelegramAccount[]> => {
    const { data } = await loadsIngestServiceAxios.get(`/telegram/groups/${encodeURIComponent(peerId)}/accounts`)
    return data?.data ?? []
}

export const addAccountToGroup = async (peerId: string, phone: string): Promise<void> => {
    await loadsIngestServiceAxios.post(`/telegram/groups/${encodeURIComponent(peerId)}/accounts`, { phone })
}

export const removeAccountFromGroup = async (peerId: string, phone: string): Promise<void> => {
    await loadsIngestServiceAxios.delete(`/telegram/groups/${encodeURIComponent(peerId)}/accounts/${encodeURIComponent(phone)}`)
}

// Account groups
const listGroupsForAccount = async (phone: string): Promise<TelegramGroup[]> => {
    const { data } = await loadsIngestServiceAxios.get(`/telegram/accounts/${encodeURIComponent(phone)}/groups`)
    return data?.data ?? []
}

export const importGroupsFromAccount = async (phone: string): Promise<{ imported: number; groups: Array<{ peerId: string; originalName: string | null }> }> => {
    const { data } = await loadsIngestServiceAxios.post(`/telegram/accounts/${encodeURIComponent(phone)}/import-groups`)
    return data?.data ?? { imported: 0, groups: [] }
}
